import React, { useEffect, useState } from 'react'
import { Modal, Table, Tag, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { actionsApi } from 'api/actions'
import noProductImage from 'assets/images/no-image-product.png'
import { ICustomFeed, IFeedProduct } from 'models/feeds'
import { productTags } from 'shared/constants/product'
import { IMAGES_URL } from 'shared/constants/urls'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showServerError } from 'shared/utils/showServerError'

interface IFeedProductsModalProps {
  feed: ICustomFeed | null
  onClose: () => void
}

const renderInFeed = (inFeed: boolean) =>
  inFeed ? <Tag color='green'>в фиде</Tag> : <Tag>не попадает</Tag>

const columns: ColumnsType<IFeedProduct> = [
  {
    title: 'Артикул',
    dataIndex: 'article',
    width: 101
  },
  {
    title: 'Фото',
    dataIndex: 'photo',
    width: 70,
    render: (_, { photo }) =>
      photo && !!photo.length ? (
        <div
          style={{
            width: 62,
            height: 56,
            backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
      ) : (
        <img
          src={noProductImage}
          alt='Изображение товара'
          width={62}
          height={56}
        />
      )
  },
  {
    title: 'Наименование товара',
    dataIndex: 'name',
    width: 310
  },
  {
    title: 'Категория',
    dataIndex: 'category_id',
    width: 250,
    render: (_, { category_id, subCategory_id }) => {
      if (!category_id || !subCategory_id) {
        return (
          <Typography.Text style={{ opacity: 0.5 }}>Не задана</Typography.Text>
        )
      }
      return (
        <>
          <Typography.Text>{category_id.name + ' >'}</Typography.Text>
          <br />
          <Typography.Text>{subCategory_id.name}</Typography.Text>
        </>
      )
    }
  },
  {
    title: 'Цена',
    dataIndex: 'price',
    width: 101,
    render: (_, { price }) => (
      <Typography.Text style={{ opacity: price ? 1 : 0.5 }}>
        {price ? getNormalizedPrice(price) : 'Не задана'}
      </Typography.Text>
    )
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    width: 136,
    render: (_, { status }) =>
      productTags[status] ? (
        <Tag color={productTags[status].color}>{productTags[status].label}</Tag>
      ) : (
        status
      )
  },
  {
    title: 'Яндекс',
    dataIndex: ['in_feed', 'yandex'],
    width: 120,
    render: (_, { in_feed }) => renderInFeed(in_feed.yandex)
  },
  {
    title: 'VK',
    dataIndex: ['in_feed', 'vk'],
    width: 120,
    render: (_, { in_feed }) => renderInFeed(in_feed.vk)
  }
]

export const FeedProductsModal: React.FC<IFeedProductsModalProps> = ({
  feed,
  onClose
}) => {
  const [products, setProducts] = useState<IFeedProduct[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!feed) return
    const loadProducts = async () => {
      try {
        setIsLoading(true)
        const response = await actionsApi.getFeedProducts(feed._id)
        setProducts(response.data)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }
    setProducts([])
    loadProducts()
  }, [feed])

  return (
    <Modal
      centered
      open={!!feed}
      title={feed ? `${feed.name}: товары` : ''}
      footer={null}
      onCancel={onClose}
      width={1100}
    >
      <Typography.Paragraph type='secondary'>
        Товар из списка попадает в выгрузку площадки, пока проходит её условия
      </Typography.Paragraph>
      <Table
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={products}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        pagination={{ pageSize: 7, showSizeChanger: false }}
      />
    </Modal>
  )
}
